import { Button, Col, Modal, Row, Steps, Tooltip, Typography } from "antd";
import { ReactNode, useEffect, useState } from "react";
import "./styles.css"
import FirstStep from "./FirstStep";
import SecondStep from "./SecondStep";
import ThirdStep from "./ThirdStep";
import FourthStep from "./FourthStep";
import { trainingAPI } from "@api/TrainingAPI";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

const { Title, Text } = Typography;

export const BuildBaselineModal = ({ visible, setVisibility, setRefetchData }: IBuildBaselineModal) => {
    const { players: teamPlayers } = useSelector(({ team }: any) => team || {});
    
    const [step, setStep] = useState<number>(0)
    const [startDate, setStartDate] = useState<string>('')
    const [endDate, setEndDate] = useState<string>('')
    const [days, setDays] = useState<string[]>([])
    const [players, setPlayers] = useState<string[]>(['full-team'])
    const [playerLabels, setPlayerLabels] = useState<string[]>(['Full Team'])
    const [applyToPlayers, setApplyToPlayers] = useState<string[]>([])
    const [applyToPlayerLabels, setApplyToPlayerLabels] = useState<string[]>([])
    const [baselines, setBaselines] = useState<{ [key: string]: number | string }>({})

    const stepData: {
        header: string;
        subHeader: string;
        component: ReactNode
        prevButton?: {
            text: string;
            logic: () => void
        },
        nextButton: {
            text: string;
            logic: () => void;
            disabled: boolean;
            tooltip?: string;
            "data-attr"?: string;
        }
    }[] = [
            {
                header: "Target Data",
                subHeader: "Please select some fitlers to narrow down your target data",
                component: <FirstStep
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    days={days}
                    setDays={setDays}
                    players={players}
                    setPlayers={setPlayers}
                    playerLabels={playerLabels}
                    setPlayerLabels={setPlayerLabels}
                />,
                nextButton: {
                    text: 'Next',
                    logic: () => setStep(step + 1),
                    disabled: (startDate === '' || endDate === '') || dayjs(endDate).diff(dayjs(startDate), 'year') > 2,
                    tooltip: "Date range needs to be selected and less than 2 years"
                }
            },
            {
                header: "View Recommendations",
                subHeader: "Click on a value in a row to populate the baseline column or enter a custom value yourself",
                component: <SecondStep
                    startDate={startDate as any}
                    endDate={endDate as any}
                    days={days}
                    players={players}
                    playerLabels={playerLabels}
                    baselines={baselines}
                    setBaselines={setBaselines}
                />,
                prevButton: {
                    text: "Previous",
                    logic: () => setStep(step - 1)
                },
                nextButton: {
                    text: 'Next',
                    logic: () => setStep(step + 1),
                    disabled: Object.values(baselines).every(e => e === ""),
                    tooltip: "All baselines need to be set"
                }
            },
            {
                header: "Review",
                subHeader: "Lets review your baseline values",
                component: <ThirdStep baselines={baselines} />,
                prevButton: {
                    text: "Previous",
                    logic: () => setStep(step - 1)
                },
                nextButton: {
                    text: 'Next',
                    logic: () => setStep(step + 1),
                    disabled: false
                }
            },
            {
                header: "Apply Baselines",
                subHeader: "Lets apply these baselines to your team",
                component: <FourthStep
                    applyToPlayers={applyToPlayers}
                    setApplyToPlayers={setApplyToPlayers}
                    applyToPlayerLabels={applyToPlayerLabels}
                    setApplyToPlayerLabels={setApplyToPlayerLabels}
                />,
                prevButton: {
                    text: "Previous",
                    logic: () => setStep(step - 1)
                },
                nextButton: {
                    text: 'Confirm',
                    "data-attr": "help-me-build-my-baselines-apply-baselines-confirm",
                    logic: async () => {
                        const baselinesToSave: any[] = [];
                        const playerIdsToUpdate = new Set<string>();
                        
                        // Process each selection (can be full-team, player-{id}, pos-{position}, or group-{id})
                        applyToPlayers.forEach((selection) => {
                            if (selection === 'full-team') {
                                // Add all non-retired players
                                (teamPlayers || []).forEach((p: any) => {
                                    if (!p?.retired && p?.player_id) {
                                        playerIdsToUpdate.add(String(p.player_id));
                                    }
                                });
                            } else if (selection.startsWith('player-')) {
                                // Add specific player
                                const playerId = selection.replace('player-', '');
                                playerIdsToUpdate.add(String(playerId));
                            } else if (selection.startsWith('pos-')) {
                                // Add all players from this position
                                const position = selection.replace('pos-', '');
                                (teamPlayers || []).forEach((p: any) => {
                                    if (!p?.retired && p?.position === position && p?.player_id) {
                                        playerIdsToUpdate.add(String(p.player_id));
                                    }
                                });
                            } else if (selection.startsWith('group-')) {
                                // Add all players from this group
                                const groupId = parseInt(selection.replace('group-', ''), 10);
                                (teamPlayers || []).forEach((p: any) => {
                                    if (!p?.retired && p?.player_groups?.includes(groupId) && p?.player_id) {
                                        playerIdsToUpdate.add(String(p.player_id));
                                    }
                                });
                            }
                        });
                        
                        // Create baseline entries for each unique player
                        playerIdsToUpdate.forEach((playerId) => {
                            const player = (teamPlayers || []).find((p: any) => String(p.player_id) === String(playerId));
                            if (player && player.player_id) {
                                const playerBaseline: any = {
                                    player_id: player.player_id,
                                    position: player.position,
                                };
                                
                                Object.entries(baselines).forEach(([key, value]) => {
                                    if (value !== '') {
                                        playerBaseline[key] = Number(value);
                                    }
                                });
                                
                                baselinesToSave.push(playerBaseline);
                            }
                        });

                        // Validate all baseline objects have player_id before saving
                        const invalidBaselines = baselinesToSave.filter(b => !b.player_id);
                        if (invalidBaselines.length > 0) {
                            console.error('Invalid baselines without player_id:', invalidBaselines);
                            return;
                        }

                        if (baselinesToSave.length > 0) {
                            await trainingAPI.updateBaseline({ baselines: baselinesToSave });
                        }

                        setVisibility(false);
                        setRefetchData(true);
                    },
                    disabled: applyToPlayers.length === 0,
                    tooltip: "Need to select players, positions, or groups"
                }
            }
        ]

    return (
        <Modal open={visible} onCancel={() => setVisibility(false)} footer={null} width={1200} bodyStyle={{ minHeight: '500px' }}>
            <Row style={{ minHeight: '500px', maxHeight: '500px' }}>
                <Col span={6}>
                    <Steps
                        className="build-baseline-modal-steps"
                        direction="vertical"
                        current={step}
                        items={[
                            {
                                title: 'Filter Data',
                                description: 'Narrow down your target data'
                            },
                            {
                                title: 'Select Baselines',
                                description: 'Choose reference values'
                            },
                            {
                                title: 'Review & Confirm',
                                description: 'Validate your selections'
                            },
                            {
                                title: 'Apply Baselines',
                                description: 'Set baselines for your positions'
                            },
                        ]}
                    />
                </Col>
                <Col span={18} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', borderLeft: 'solid lightgray 1px', paddingLeft: '30px' }}>
                    <Col>
                        <Col style={{ marginBottom: '20px' }}>
                            <Row>
                                <Title level={2} style={{ marginBottom: '0px' }}>{stepData[step].header}</Title>
                            </Row>
                            <Row>
                                <Text type="secondary">{stepData[step].subHeader}</Text>
                            </Row>
                        </Col>
                        {stepData[step].component}
                    </Col>
                    <Row style={{ justifyContent: "flex-end", columnGap: "20px" }}>
                        {stepData[step].prevButton &&
                            <Button type="default" onClick={stepData[step].prevButton.logic}>{stepData[step].prevButton.text}</Button>
                        }
                        {stepData[step].nextButton &&
                            <Tooltip title={(stepData[step].nextButton.tooltip && stepData[step].nextButton.disabled) && stepData[step].nextButton.tooltip}>
                                <Button
                                    type="primary"
                                    onClick={stepData[step].nextButton.logic}
                                    disabled={stepData[step].nextButton.disabled}
                                    data-attr={stepData[step].nextButton["data-attr"]}
                                >
                                    {stepData[step].nextButton.text}
                                </Button>
                            </Tooltip>
                        }
                    </Row>
                </Col>
            </Row>
        </Modal>
    )
}

interface IBuildBaselineModal {
    visible: boolean;
    setVisibility: React.Dispatch<boolean>
    setRefetchData: React.Dispatch<boolean>
}

export default BuildBaselineModal;